<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>User</template>
            <template v-slot:pageDetail>List</template>
        </o-bread-crumb>

        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>

        <!-- Main Content -->
        <div v-if="thisComponentShow" class="card text-center">
            <!-- Header -->
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item">
                        <div class="d-flex d-inline-block">
                            <a class="nav-link text-dark fs-4" href="#">List of Users</a>
                        </div>
                    </li>
                    <li class="nav-item btn btn-primary btn-sm" v-if="write">
                        <router-link :to="{ name: 'UserForm' }" class="nav-link text-white">
                            Add new user
                        </router-link>
                    </li>
                </ul>
            </div>

            <!-- Body -->
            <div class="card-body">
                <div class="d-flex justify-content-center spinner-border text-primary spinner-position" v-if="page.isLoading"></div>
                <div class="spinner" v-if='page.isLoading'></div>

                <!-- Content -->
                <div v-if="page.output">
                    <!-- Search and Reload Controls -->
                    <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                        <li class="nav-item mb-2">
                            <div class="d-flex d-inline-block">
                                <o-search 
                                    :searchOptions="searchOptions" 
                                    :levelOptions="levelOptions" 
                                    @submit="handleSearch" 
                                    @clear="handleClearSearch"
                                />
                            </div>
                        </li>
                        <li class="nav-item btn btn-primary btn-sm" style="height: 50px;">
                            <button 
                                class="reload nav-link text-white" 
                                @click.prevent="loadState"
                                :disabled="page.isLoading"
                            >
                                &#x21bb;
                            </button>
                        </li>
                    </ul>

                    <!-- Users Table -->
                    <o-table style="text-align:left;" class="table-hover">
                        <thead>
                            <tr>
                                <td 
                                    v-for="(field, index) in fields" 
                                    :key="index"
                                    class="fw-bolder text-muted" 
                                    scope="col"
                                >
                                    {{ field }}
                                </td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in users" :key="item.id || index">
                                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                <td>{{ formatUserName(item) }}</td>
                                <td>{{ item.email }}</td>
                                <td>{{ item.levels?.[0]?.name || 'N/A' }}</td>
                                <td class="ml-3">
                                    <div class="d-flex">
                                        <div class="me-1" v-if="write">
                                            <button class="btn btn-sm btn-info rounded-pill text-white">
                                                <router-link 
                                                    class="text-white" 
                                                    :to="{ name: 'UserEdit', params: { id: item.id } }"
                                                >
                                                    Edit
                                                </router-link>
                                            </button>
                                        </div>
                                        <div v-if="write">
                                            <button 
                                                class="btn btn-sm btn-danger rounded-pill text-white"
                                                @click.prevent="confirmDelete(item)"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </o-table>
                </div>
            </div>

            <!-- Pagination -->
            <div class="container mt-5">
                <o-pagination 
                    :page="meta.page" 
                    :total="meta.total" 
                    @callback="handlePageNavigation"
                />
            </div>
        </div>
        
        <o-warning-box
            v-if="isDeleteConfirmVisible"
            title="Delete User"
            :item-name="userToDelete ? formatUserName(userToDelete) : 'this user'"
            warning-message="This action cannot be undone."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            :is-loading="page.isLoading"
            @confirm="handleDelete"
            @cancel="closeDeleteConfirm"
        />
    </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { OTable, OPagination, OBreadCrumb, OSearch, OWarningBox } from '@/components/OmnihControl'

export default {
    name: 'UserList',
    components: {
        OTable,
        OPagination,
        OBreadCrumb,
        OSearch,
        OWarningBox
    },
    setup() {
        const store = useStore()
        
        // Reactive state
        const page = reactive({
            isLoading: false,
            output: false,
            component: 'user'
        })

        const error = ref(null)
        const thisComponentShow = ref(true)
        const write = ref(false)
        const lastActivePage = ref(1)

        // Computed properties
        const users = computed(() => store.state.User.users)
        const meta = computed(() => store.state.User.meta)
        const selfLoaded = computed(() => store.state.Auth.selfLoaded)

        // Constants
        const fields = ref([
            'No',
            'Name',
            'Email',
            'Levels',
            'action'
        ])

        const searchOptions = ref([
            { value: 'name', text: 'name' },
            { value: 'email', text: 'email' },
            { value: 'levels', text: 'level' }
        ])

        const levelOptions = ref([
            { value: '1', text: 'corporate' },
            { value: '2', text: 'branch' },
            { value: '3', text: 'merchant' }
        ])

        // Methods
        const formatUserName = (user) => {
            return `${user.title || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()
        }

        const checkUserPermissions = () => {
            if (store.state.Auth.currUser.level === 'root') {
                write.value = true
                return
            }

            const userModules = store.state.Auth.userInfo.modules
            if (userModules) {
                const matchingModule = userModules.find(module => module.module_name === page.component)
                write.value = matchingModule ? matchingModule.write : false
            } else {
                console.error('userModules is undefined')
                write.value = false
            }
        }

        const createEntityFilter = (businessProfile) => {
            const filter = {}
            
            if (businessProfile.corporate_id) {
                filter.entity = `corporate,${businessProfile.corporate_id}`
            } else if (businessProfile.branch_id) {
                filter.entity = `branch,${businessProfile.branch_id}`
            }
            
            return filter
        }

        const loadState = async () => {
            if (page.isLoading) return // Prevent multiple simultaneous requests
            
            page.isLoading = true
            error.value = null

            try {
                const businessProfile = await store.dispatch('Auth/getBusinessProfile')
                const filter = createEntityFilter(businessProfile)
                
                store.commit('User/add_filter', { filter })
                await store.dispatch('User/read')
                
                checkUserPermissions()
                page.output = true
            } catch (err) {
                console.error('Error loading users:', err)
                error.value = err.response?.data?.message || err.message || 'An error occurred'
                thisComponentShow.value = false
            } finally {
                page.isLoading = false
            }
        }

        const isDeleteConfirmVisible = ref(false)
        const userToDelete = ref(null)

        const confirmDelete = (item) => {
            userToDelete.value = item
            isDeleteConfirmVisible.value = true
        }

        const closeDeleteConfirm = () => {
            isDeleteConfirmVisible.value = false
            userToDelete.value = null
        }

        const handleDelete = async () => {
            if (!userToDelete.value) return

            page.isLoading = true
            error.value = null

            try {
                await store.dispatch('User/delete', userToDelete.value.id)
                await loadState()
                closeDeleteConfirm()
            } catch (err) {
                console.error('Error deleting user:', err)
                error.value = err.message || 'Failed to delete user'
                closeDeleteConfirm()
            } finally {
                page.isLoading = false
            }
        }

        const handlePageNavigation = (newPage) => {
            const updatedMeta = { ...meta.value, page: newPage }
            store.commit('User/switch_page', { meta: updatedMeta })
            loadState()
        }

        const handleSearch = async (searchBy) => {
            page.isLoading = true
            
            try {
                if (!searchBy.selected) {
                    return // Exit early but loading will be set to false in finally
                }

                // Clear criteria for levels search
                if (searchBy.selected === 'levels') {
                    searchBy.criteria = ""
                }

                const search = {}
                
                if (searchBy.criteria && searchBy.selected !== 'level') {
                    search[searchBy.selected] = searchBy.criteria
                } else if (searchBy.level) {
                    search[searchBy.selected] = searchBy.level
                }

                lastActivePage.value = meta.value.page

                // Reset to first page for new search
                const updatedMeta = { ...meta.value, page: 1 }
                store.commit('User/switch_page', { meta: updatedMeta })
                store.commit('User/set_search', { search })
                
                await store.dispatch('User/read')
                
                // Clear search form
                searchBy.criteria = ""
                searchBy.selected = ""
            } catch (err) {
                console.error('Error during search:', err)
                error.value = err.message || 'Search failed'
            } finally {
                page.isLoading = false
            }
        }

        const handleClearSearch = async () => {
            page.isLoading = true
            
            try {
                const updatedMeta = { ...meta.value, page: lastActivePage.value }
                store.commit('User/switch_page', { meta: updatedMeta })
                store.commit('User/clear_search')
                await store.dispatch('User/read')
            } catch (err) {
                console.error('Error clearing search:', err)
                error.value = err.message || 'Clear search failed'
            } finally {
                page.isLoading = false
            }
        }

        // Watchers
        watch(
            () => store.state.Auth.selfLoaded,
            (newVal) => {
                if (newVal) {
                    loadState()
                }
            }
        )

        // Function to calculate and navigate to last page
        const goToLastPage = () => {
            const totalPages = meta.value.total
            if (totalPages && totalPages > 0) {
                const updatedMeta = { ...meta.value, page: totalPages }
                store.commit('User/switch_page', { meta: updatedMeta })
                loadState()
            }
        }

        // Lifecycle hooks
        onMounted(() => {
            // Check if redirected from create/edit with newItem flag
            const urlParams = new URLSearchParams(window.location.search)
            const isNewItem = urlParams.get('newItem') === 'true'
            
            if (selfLoaded.value) {
                loadState().then(() => {
                    if (isNewItem) {
                        goToLastPage()
                        // Clean up URL parameter
                        window.history.replaceState({}, '', window.location.pathname)
                    }
                })
            }
        })

        onBeforeUnmount(() => {
            const updatedMeta = { ...meta.value, page: lastActivePage.value }
            store.commit('User/switch_page', { meta: updatedMeta })
            store.commit('User/clear_search')
            store.dispatch('User/read')
        })

        return {
            // State
            page,
            error,
            thisComponentShow,
            write,
            
            // Computed
            users,
            meta,
            
            // Constants
            fields,
            searchOptions,
            levelOptions,
            
            // Methods
            formatUserName,
            loadState,
            confirmDelete,
            handleDelete,
            closeDeleteConfirm,
            handlePageNavigation,
            handleSearch,
            handleClearSearch,
            isDeleteConfirmVisible,
            userToDelete
        }
    }
}
</script>

<style scoped>
.form-group {
    width: 250px;
    text-align: left;
    padding-left: 16px;
}

.reload:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>